import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';

import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { dashboardRoutes } from '../../constants/routes';
import { Stepper } from './components/Stepper';

import {
  Step1PersonalInformation,
  type DriverDetailsData,
} from './steps/Step1PersonalInformation';

import {
  Step2LicenseDocument,
  type DriverLicenseData,
} from './steps/Step2LicenseDocument';

import { Step3VehicleSelection } from './steps/Step3VehicleSelection';
import { Step4ReviewConfirm } from './steps/Step4ReviewConfirm';

import { useDrivers } from '../../hooks/useDrivers';

import {
  apiGetDriverById,
  type CreateDriverPayload,
} from '../../api/drivers/driversApi';

export const NewDriverPage = () => {
  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const {
    addDriver,
    updateDriver,
  } = useDrivers();

  const isEditMode = Boolean(id);

  const [currentStep, setCurrentStep] = useState(1);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isLoadingDriver, setIsLoadingDriver] = useState(false);

  const currentUser = {
    name: 'Admin',
    email: 'Admin@123gmail.com',
    avatar: 'https://i.pravatar.cc/150?img=68',
  };

  // ---------------------------------------
  // Step 1 - Personal Information
  // ---------------------------------------

  const [driverData, setDriverData] =
    useState<DriverDetailsData>({
      firstName: '',
      lastName: '',
      driverId: '',
      gender: '',
      languages: '',
      mobileNumber: '',
      emailAddress: '',
    });

  const updateDriverData = (
    data: Partial<DriverDetailsData>
  ) => {
    setDriverData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  // ---------------------------------------
  // Step 2 - License & Documents
  // ---------------------------------------

  const [licenseData, setLicenseData] =
    useState<DriverLicenseData>({
      licenseNumber: '',
      issueDate: '',
      expiryDate: '',
      driverPhoto: null,
      drivingLicense: null,
      passportId: null,
      vehicleInsurance: null,
      policeClearance: null,
      medicalCertificate: null,
    });

  const updateLicenseData = (
    data: Partial<DriverLicenseData>
  ) => {
    setLicenseData((prev) => ({
      ...prev,
      ...data,
    }));
  };

  // ---------------------------------------
  // Step 3 - Vehicle
  // ---------------------------------------

  // This is the REAL Vehicle/Fleet database ID.
  const [
    selectedVehicleId,
    setSelectedVehicleId,
  ] = useState<string | null>(null);

  // ---------------------------------------
  // Edit Driver
  // ---------------------------------------

  useEffect(() => {
    if (!id) {
      return;
    }

    const populateFromDriver = (
      driver: any
    ) => {
      const fullName =
        driver.driver_name ||
        driver.name ||
        '';

      const nameParts =
        String(fullName)
          .trim()
          .split(/\s+/)
          .filter(Boolean);

      const fallbackFirstName =
        nameParts[0] || '';

      const fallbackLastName =
        nameParts.slice(1).join(' ');

      setDriverData({
        firstName:
          driver.user_first_name ||
          driver.first_name ||
          driver.firstName ||
          fallbackFirstName,

        lastName:
          driver.user_last_name ||
          driver.last_name ||
          driver.lastName ||
          fallbackLastName,

        driverId:
          driver.driver_id ||
          driver.driverId ||
          driver.code ||
          '',

        gender:
          driver.gender || '',

        languages:
          driver.languages ||
          driver.language ||
          '',

        mobileNumber:
          driver.user_mobile_number ||
          driver.mobile_number ||
          driver.mobileNumber ||
          driver.phone ||
          '',

        emailAddress:
          driver.user_email ||
          driver.email ||
          driver.emailAddress ||
          '',
      });

      const docs = Array.isArray(driver.documents)
        ? driver.documents
        : [];

      const findDoc = (type: string) => {
        const d = docs.find(
          (item: any) =>
            item.document_type === type ||
            item.documentType === type
        );

        return d?.file || null;
      };

      setLicenseData((previous) => ({
        ...previous,

        licenseNumber:
          driver.license_number ||
          driver.licenseNumber ||
          '',

        issueDate:
          driver.license_issue_date ||
          driver.licenseIssueDate ||
          driver.issueDate ||
          '',

        expiryDate:
          driver.license_expiry_date ||
          driver.licenseExpiryDate ||
          driver.expiryDate ||
          '',

        driverPhoto:
          driver.driverPhoto ||
          driver.photo ||
          driver.avatar ||
          findDoc('driver_photo') ||
          previous.driverPhoto,

        drivingLicense:
          driver.drivingLicense ||
          findDoc('driving_license') ||
          previous.drivingLicense,

        passportId:
          driver.passportId ||
          findDoc('passport_id') ||
          previous.passportId,

        vehicleInsurance:
          driver.vehicleInsurance ||
          findDoc('vehicle_insurance') ||
          previous.vehicleInsurance,

        policeClearance:
          driver.policeClearance ||
          findDoc('police_clearance') ||
          previous.policeClearance,

        medicalCertificate:
          driver.medicalCertificate ||
          findDoc('medical_certificate') ||
          previous.medicalCertificate,
      }));

      let realVehicleId: string | null = null;

      if (
        driver.vehicle !== null &&
        driver.vehicle !== undefined &&
        driver.vehicle !== ''
      ) {
        if (
          typeof driver.vehicle === 'object'
        ) {
          const vehicleId =
            driver.vehicle.id ??
            driver.vehicle.pk;

          if (
            vehicleId !== undefined &&
            vehicleId !== null
          ) {
            realVehicleId =
              String(vehicleId);
          }
        } else {
          realVehicleId =
            String(driver.vehicle);
        }
      }

      setSelectedVehicleId(
        realVehicleId
      );
    };

    // Always fetch complete driver details
    // from the detail API when editing.
    //
    // The drivers list contains normalized
    // card data only and does not contain all
    // editable fields.

    setIsLoadingDriver(true);

    apiGetDriverById(id)
      .then((res) => {
        if (res.success && res.data) {
          populateFromDriver(res.data);
        } else {
          toast.error(
            res.message ||
              'Failed to load driver.'
          );
        }
      })
      .catch((error) => {
        console.error(
          'Failed to load driver:',
          error
        );

        toast.error(
          'Failed to load driver.'
        );
      })
      .finally(() => {
        setIsLoadingDriver(false);
      });
  }, [id]);

  // ---------------------------------------
  // Scroll to top when changing steps
  // ---------------------------------------

  useEffect(() => {
    const scrollContainer = document.querySelector(
      '.overflow-y-auto.overscroll-contain'
    ) as HTMLElement | null;

    scrollContainer?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [currentStep]);

  // ---------------------------------------
  // Next / Submit
  // ---------------------------------------

  const handleNext = async () => {
    if (currentStep < 4) {
      setCurrentStep(
        (prev) => prev + 1
      );

      return;
    }

    // -----------------------------------
    // Final submission
    // -----------------------------------

    setIsSubmitting(true);

    try {
      // -----------------------------------
      // Prepare API payload
      // -----------------------------------

      const payload: CreateDriverPayload = {
        firstName: driverData.firstName,
        lastName: driverData.lastName,
        driverId: driverData.driverId,
        gender: driverData.gender,
        languages: driverData.languages,
        mobileNumber: driverData.mobileNumber,
        emailAddress: driverData.emailAddress,

        licenseNumber: licenseData.licenseNumber,
        issueDate: licenseData.issueDate,
        expiryDate: licenseData.expiryDate,

        driverPhoto: licenseData.driverPhoto,
        drivingLicense: licenseData.drivingLicense,
        passportId: licenseData.passportId,
        vehicleInsurance: licenseData.vehicleInsurance,
        policeClearance: licenseData.policeClearance,
        medicalCertificate:
          licenseData.medicalCertificate,

        vehicle:
          selectedVehicleId || undefined,
      };

      console.log(
        '========== DRIVER PAYLOAD =========='
      );

      console.log(
        'Driver:',
        driverData
      );

      console.log(
        'License:',
        licenseData
      );

      console.log(
        'Selected Vehicle ID:',
        selectedVehicleId
      );

      console.log(
        'Payload:',
        payload
      );

      console.log(
        '===================================='
      );

      // -----------------------------------
      // EDIT
      // -----------------------------------

      if (isEditMode && id) {
        const res = await updateDriver(
          id,
          payload
        );

        if (res.success) {
          toast.success(
            'Driver updated successfully!'
          );

          navigate(
            dashboardRoutes.drivers
          );
        } else {
          toast.error(
            res.message ||
              'Failed to update driver.'
          );
        }

        return;
      }

      // -----------------------------------
      // CREATE
      // -----------------------------------

      const res = await addDriver(
        payload
      );

      if (res.success) {
        toast.success(
          'Driver added successfully!'
        );

        navigate(
          dashboardRoutes.drivers
        );
      } else {
        console.error(
          'Create driver failed:',
          res
        );

        toast.error(
          res.message ||
            'Failed to add driver.'
        );
      }
    } catch (error: any) {
      console.error(
        'Driver submission error:',
        error
      );

      toast.error(
        error?.message ||
          'An unexpected error occurred.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------------------------------------
  // Back
  // ---------------------------------------

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(
        (prev) => prev - 1
      );
    } else {
      navigate(
        dashboardRoutes.drivers
      );
    }
  };

  // ---------------------------------------
  // Validation
  // ---------------------------------------

  const isStep1Valid =
    driverData.firstName.trim() !== '' &&
    driverData.lastName.trim() !== '' &&
    driverData.gender !== '' &&
    driverData.languages.trim() !== '' &&
    driverData.mobileNumber.trim() !== '' &&
    driverData.emailAddress.trim() !== '';

  // Licence fields are optional.
  const isStep2Valid = true;

  // Vehicle selection is optional.
  const isStep3Valid = true;

  const isCurrentStepValid =
    currentStep === 1
      ? isStep1Valid
      : currentStep === 2
        ? isStep2Valid
        : currentStep === 3
          ? isStep3Valid
          : true;

  // ---------------------------------------
  // Render
  // ---------------------------------------

  return (
    <DashboardLayout
      title={
        isEditMode
          ? 'Edit Driver'
          : 'Add New Driver'
      }

      subtitle={
        <div className="flex items-center gap-[4px] text-[14px] font-host">

          <span
            className="cursor-pointer text-[#9E9E9E] hover:text-[#1A1A1A] transition-colors"
            onClick={() =>
              navigate(
                dashboardRoutes.overview
              )
            }
          >
            Dashboard
          </span>

          <span className="text-[#9E9E9E]">
            &gt;
          </span>

          <span
            className="cursor-pointer text-[#9E9E9E] hover:text-[#1A1A1A] transition-colors"
            onClick={() =>
              navigate(
                dashboardRoutes.drivers
              )
            }
          >
            Drivers
          </span>

          <span className="text-[#9E9E9E]">
            &gt;
          </span>

          <span className="font-medium text-[#1A1A1A]">
            {isEditMode
              ? 'Edit Driver'
              : 'Add New Driver'}
          </span>

        </div>
      }

      activeNavId="drivers"

      user={currentUser}
    >
      <div className="flex flex-col w-full pb-[120px]">

        {/* Stepper */}

        <div className="sticky top-0 z-20 bg-dash-bg pt-4 pb-6 px-[16px] sm:px-[24px] xl:px-0 -mx-[16px] sm:-mx-[24px] xl:mx-0">

          <Stepper
            currentStep={currentStep}
          />

        </div>

        {/* Step Content */}

        {isLoadingDriver ? (

          <div className="w-full flex items-center justify-center py-20">

            <Loader2
              className="animate-spin text-[#FF6A1A]"
              size={32}
            />

          </div>

        ) : (

          <div className="w-full mt-6">

            {/* Step 1 */}

            {currentStep === 1 && (
              <Step1PersonalInformation
                data={driverData}
                updateData={
                  updateDriverData
                }
              />
            )}

            {/* Step 2 */}

            {currentStep === 2 && (
              <Step2LicenseDocument
                data={licenseData}
                updateData={
                  updateLicenseData
                }
              />
            )}

            {/* Step 3 */}

            {currentStep === 3 && (
              <Step3VehicleSelection
                selectedVehicleId={
                  selectedVehicleId
                }
                onSelectVehicle={
                  setSelectedVehicleId
                }
              />
            )}

            {/* Step 4 */}

            {currentStep === 4 && (
              <Step4ReviewConfirm
                driverData={driverData}
                licenseData={licenseData}
                selectedVehicleId={
                  selectedVehicleId
                }
              />
            )}

          </div>
        )}

        {/* Fixed Footer */}

        <div className="fixed bottom-0 left-0 right-0 h-[80px] bg-[#FAFAFA] border-t border-[#E0E0E0] flex items-center justify-center z-50 lg:ml-[213px]">

          <div className="w-full max-w-[1000px] px-[32px] flex items-center justify-between mx-auto">

            <button
              type="button"
              onClick={handleBack}
              disabled={isSubmitting}
              className="w-[140px] flex items-center justify-center gap-2 h-[48px] rounded-[24px] bg-black text-white font-host text-[15px] font-bold transition-colors hover:bg-gray-800 disabled:opacity-50"
            >
              <ArrowLeft
                size={18}
                className="text-[#FF6A1A]"
              />

              Back
            </button>

            <button
              type="button"
              onClick={handleNext}
              disabled={
                !isCurrentStepValid ||
                isSubmitting
              }
              className={`w-[160px] flex items-center justify-center gap-2 h-[48px] rounded-[24px] font-host text-[15px] font-bold transition-colors ${
                isCurrentStepValid &&
                !isSubmitting
                  ? 'bg-[#FF6A1A] text-white hover:bg-[#E55A12]'
                  : 'bg-[#E0E0E0] text-[#9E9E9E] cursor-not-allowed'
              }`}
            >

              {isSubmitting ? (

                <>
                  <Loader2
                    size={18}
                    className="animate-spin"
                  />

                  Saving...
                </>

              ) : currentStep === 4 ? (

                isEditMode
                  ? 'Save Changes'
                  : 'Add Driver'

              ) : (

                'Next'

              )}

            </button>

          </div>

        </div>

      </div>
    </DashboardLayout>
  );
};
